/**
 * Il testo dell'annuncio, modificabile a mano.
 *
 * Nove campi, quelli che Guesty espone e che i canali pubblicano: dal titolo
 * alla descrizione, dalle regole della casa a come si arriva. Prima qui c'era
 * solo il titolo in sola lettura, e non serviva a niente.
 *
 * **Salvare non scrive.** Costruisce una proposta con il prima/dopo campo per
 * campo, esattamente come se l'avessi chiesta in chat: approvi, e da lì il
 * testo parte verso il gestionale e poi sui canali. È la regola numero uno del
 * prodotto, e non cambia perché la modifica arriva da un modulo.
 */

import { useEffect, useMemo, useState } from "react";
import { Check, RotateCcw, Send, Sparkles } from "lucide-react";
import {
  LISTING_TITLE_MAX_LENGTH,
  type ListingFields,
  type ListingView,
} from "@otto/shared";
import { api } from "../lib/api.ts";
import { messaggioDi } from "../lib/errori.ts";
import { useStore } from "../lib/store.tsx";
import { Badge, Card, Pill, cx } from "../ui/primitives.tsx";

/** I campi, nell'ordine in cui un ospite li legge. */
const CAMPI: Array<{
  id: keyof ListingFields;
  label: string;
  aiuto: string;
  righe: number;
  max?: number;
}> = [
  {
    id: "title",
    label: "Titolo",
    aiuto: `Quello che si vede nei risultati di ricerca. Airbnb taglia oltre i ${LISTING_TITLE_MAX_LENGTH} caratteri.`,
    righe: 2,
    max: LISTING_TITLE_MAX_LENGTH,
  },
  {
    id: "summary",
    label: "Descrizione",
    aiuto:
      "Le prime due righe decidono se continuano a leggere: spazio, zona, e a chi è adatto.",
    righe: 6,
  },
  {
    id: "space",
    label: "Lo spazio",
    aiuto: "Com'è fatta la casa: stanze, letti, cosa c'è.",
    righe: 4,
  },
  {
    id: "access",
    label: "Accesso e check-in",
    aiuto: "Come si entra, a che ora, con cosa.",
    righe: 3,
  },
  {
    id: "neighborhood",
    label: "La zona",
    aiuto: "Cosa c'è intorno, e perché conviene stare lì.",
    righe: 3,
  },
  {
    id: "transit",
    label: "Come arrivare",
    aiuto:
      "Mezzi, aeroporto, parcheggio. La prima domanda di chi viene in auto.",
    righe: 3,
  },
  {
    id: "houseRules",
    label: "Regole della casa",
    aiuto: "Orari, animali, fumo, feste.",
    righe: 3,
  },
  {
    id: "interactionWithGuests",
    label: "Come ti fai trovare",
    aiuto: "Quanto sei presente e come ti si contatta.",
    righe: 2,
  },
  {
    id: "notes",
    label: "Altre note",
    aiuto: "Quello che non sta altrove ma va detto.",
    righe: 3,
  },
];

export function AnnuncioCard({
  listing,
  onAskOtto,
}: {
  listing: ListingView;
  onAskOtto: (prompt: string) => void;
}) {
  const store = useStore();
  const [bozza, setBozza] = useState<ListingFields>({});
  const [salvataggio, setSalvataggio] = useState(false);

  // Ripartire dal testo pubblicato ogni volta che si cambia casa.
  useEffect(() => {
    setBozza({
      ...listing.fields,
      title: listing.fields.title ?? listing.title ?? "",
    });
  }, [listing]);

  const pubblicato = useMemo(
    () => ({
      ...listing.fields,
      title: listing.fields.title ?? listing.title ?? "",
    }),
    [listing],
  );

  /** Solo i campi che l'host ha davvero toccato: gli altri non si propongono. */
  const cambiati = useMemo(
    () =>
      CAMPI.filter(
        (campo) => (bozza[campo.id] ?? "") !== (pubblicato[campo.id] ?? ""),
      ).map((c) => c.id),
    [bozza, pubblicato],
  );

  const proponi = async () => {
    if (cambiati.length === 0) return;
    setSalvataggio(true);
    try {
      const fields: ListingFields = {};
      for (const id of cambiati) fields[id] = bozza[id] ?? "";

      await api.proponiContenuto(listing.id, fields);
      await store.refreshActions();
      store.notify(
        "Ho preparato la modifica del testo: la trovi fra le proposte da confermare. " +
          "Appena approvi, parte verso il gestionale e da lì sui canali.",
      );
    } catch (error) {
      store.notify(messaggioDi(error));
    } finally {
      setSalvataggio(false);
    }
  };

  return (
    // Su telefono senza cornice: così è allineata a Calendario e Conti, che
    // non ce l'hanno.
    <Card className="flex flex-col gap-4 p-5 max-sm:bg-transparent max-sm:p-0 max-sm:ring-0">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h3 className="text-[14px] font-extrabold text-ink">
            Testo dell'annuncio
          </h3>
          <p className="mt-0.5 max-w-[560px] text-[12.5px] leading-relaxed text-ink-soft">
            Quello che gli ospiti leggono su{" "}
            {listing.channels.join(", ") || "i canali"}. Non scrivo niente da
            solo: quando salvi ti preparo la proposta col prima/dopo, e parte
            solo se l'approvi.
          </p>
        </div>

        <div className="flex shrink-0 items-center gap-2">
          {cambiati.length > 0 ? (
            <>
              <Badge tone="accent">
                {cambiati.length} campo{cambiati.length === 1 ? "" : "i"}{" "}
                modificat
                {cambiati.length === 1 ? "o" : "i"}
              </Badge>
              <Pill
                size="sm"
                variant="ghost"
                icon={<RotateCcw size={14} />}
                onClick={() => setBozza(pubblicato)}
              >
                Annulla
              </Pill>
            </>
          ) : null}
          <Pill
            size="sm"
            variant="primary"
            icon={<Send size={14} />}
            loading={salvataggio}
            onClick={proponi}
            className={
              cambiati.length === 0
                ? "pointer-events-none opacity-40"
                : undefined
            }
          >
            Proponi la modifica
          </Pill>
        </div>
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        {CAMPI.map((campo) => {
          const valore = bozza[campo.id] ?? "";
          const modificato = cambiati.includes(campo.id);
          const troppoLungo = campo.max != null && valore.length > campo.max;

          return (
            <div
              key={campo.id}
              className={campo.id === "summary" ? "lg:col-span-2" : undefined}
            >
              <div className="mb-1 flex items-baseline justify-between gap-2">
                <label
                  htmlFor={`campo-${campo.id}`}
                  className="text-[12.5px] font-bold text-ink"
                >
                  {campo.label}
                  {modificato ? (
                    <span className="ml-1.5 text-accent">•</span>
                  ) : null}
                </label>
                {campo.max != null ? (
                  <span
                    className={cx(
                      "text-[11px] tabular-nums",
                      troppoLungo ? "font-bold text-danger" : "text-ink-faint",
                    )}
                  >
                    {valore.length}/{campo.max}
                  </span>
                ) : null}
              </div>

              <textarea
                id={`campo-${campo.id}`}
                rows={campo.righe}
                value={valore}
                onChange={(event) =>
                  setBozza((b) => ({ ...b, [campo.id]: event.target.value }))
                }
                placeholder="vuoto"
                className={cx(
                  "scroll-soft w-full resize-y rounded-field bg-surface-muted px-3 py-2.5",
                  "text-[13.5px] leading-relaxed text-ink outline-none placeholder:text-ink-faint",
                  "transition-all duration-quick ring-1",
                  troppoLungo
                    ? "ring-danger/40"
                    : modificato
                      ? "ring-accent-line"
                      : "ring-transparent focus:ring-line-strong",
                )}
              />

              <div className="mt-1 flex items-start justify-between gap-2">
                <p className="text-[11.5px] leading-snug text-ink-faint">
                  {campo.aiuto}
                </p>
                <button
                  type="button"
                  onClick={() =>
                    onAskOtto(
                      valore.trim().length === 0
                        ? `Scrivi "${campo.label}" per l'annuncio di "${listing.nickname}"`
                        : `Riscrivi meglio "${campo.label}" dell'annuncio di "${listing.nickname}"`,
                    )
                  }
                  className="flex shrink-0 items-center gap-1 text-[11.5px] font-bold text-accent transition-opacity duration-quick hover:opacity-70"
                >
                  <Sparkles size={11} />
                  {valore.trim().length === 0
                    ? "fallo scrivere a Otto"
                    : "fallo riscrivere"}
                </button>
              </div>
            </div>
          );
        })}
      </div>

      {cambiati.length > 0 ? (
        <div className="flex items-center gap-2 rounded-card bg-accent-soft px-3.5 py-2.5 ring-1 ring-accent-line">
          <Check size={15} className="shrink-0 text-accent" />
          <p className="text-[12.5px] leading-snug text-ink-soft">
            Da qui la modifica va sul gestionale, e dal gestionale su{" "}
            {listing.channels.join(", ") || "i canali collegati"}. Ti dico io
            quando è arrivata.
          </p>
        </div>
      ) : null}
    </Card>
  );
}
